import React, { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, RefreshCw, Search, UserSearch, X } from 'lucide-react';
import { Button } from '../Button';
import { useRememberedState } from '../../hooks/useRemembered';
import {
    ARBEITSFORM_LABEL,
    LEERER_FILTER,
    STATUS_LABEL,
    anzahlErreicht,
    eintraegeFuer,
    filterAktiv,
    filtere,
    heuteIso,
    kampagnenAus,
    kopfZahlen,
    legendeRollen,
    rolleLabel,
    rollenAus,
    type Punkt,
    type StatusFilter,
    type TalentFilter,
} from '../../services/talentpool/talentpoolLogik';
import { TALENT_ARBEITSFORMEN, TALENT_STATUS, type TalentStatus } from '../../services/talentpool/talentpoolTypes';
import { TALENTPOOL_GRENZE } from '../../services/talentpool/talentpoolService';
import { TalentpoolDetail } from './TalentpoolDetail';
import { TalentpoolKarte } from './TalentpoolKarte';
import { TalentpoolListe } from './TalentpoolListe';
import { useTalentpool } from './useTalentpool';

/**
 * Bereich „Talentpool“ (docs/talentpool-plan.md): Bewerbungen von
 * gefas-mbh.de als Liste links und Karte rechts. Ein Klick in die Karte setzt
 * einen Suchpunkt; die Liste sortiert dann nach Entfernung und zeigt, wer den
 * Punkt mit seinem Umkreis erreicht. Eine gewählte Bewerbung öffnet ihr
 * Detail an der Stelle der Liste, die Karte zeigt ihren Ring.
 *
 * Dünner Container — Daten in `useTalentpool`, Regeln in `talentpoolLogik`.
 * Die Wache in App.tsx lässt nur mit Recht `talentpool.verwalten` herein; die
 * Datenbank prüft es bei jedem Lesen (RLS) und Schreiben (RPC) noch einmal.
 */

export interface TalentpoolBereichProps {
    /** Zahl der neuen Bewerbungen nach jedem Stand — für das Abzeichen im Menü. */
    onNeuGeaendert?: (anzahl: number) => void;
}

const uhrzeit = (ms: number): string =>
    new Date(ms).toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin', hour: '2-digit', minute: '2-digit' });

const AUSWAHL = 'min-h-[40px] px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-700 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none';

export const TalentpoolBereich: React.FC<TalentpoolBereichProps> = ({ onNeuGeaendert }) => {
    const tp = useTalentpool(onNeuGeaendert);
    const { zeilen, laedt, ladeFehler, nichtEingerichtet, standMs, gekappt, neuLaden, speichern: speichereTalent, loeschen: loescheTalent } = tp;

    const [filter, setFilter] = useRememberedState<TalentFilter>('talentpool.filter', LEERER_FILTER);
    const [alleRinge, setAlleRinge] = useRememberedState<boolean>('talentpool.alleRinge', false);
    const [suchpunkt, setSuchpunkt] = useState<Punkt | null>(null);
    const [gewaehltId, setGewaehltId] = useState<string | null>(null);
    const [fokus, setFokus] = useState<{ id: string; nr: number } | null>(null);
    /** Rückmeldung nach dem Löschen (Anhänge, die liegen blieben). */
    const [meldung, setMeldung] = useState<{ ton: 'ok' | 'warnung'; text: string } | null>(null);

    // Bezug für die Löschfrist: der Tag des letzten Ladens, nicht die Uhr beim
    // Rendern — nach Mitternacht lädt ohnehin jemand neu.
    const heute = standMs > 0 ? heuteIso(new Date(standMs)) : '';

    const alle = useMemo(() => zeilen ?? [], [zeilen]);
    const gefiltert = useMemo(() => filtere(alle, filter, heute), [alle, filter, heute]);
    const eintraege = useMemo(() => eintraegeFuer(gefiltert, suchpunkt), [gefiltert, suchpunkt]);
    const zahlen = useMemo(() => kopfZahlen(alle, heute), [alle, heute]);
    const kampagnen = useMemo(() => kampagnenAus(alle), [alle]);
    const rollen = useMemo(() => rollenAus(alle), [alle]);
    const legende = useMemo(() => legendeRollen(alle), [alle]);

    const gewaehlt = gewaehltId ? alle.find(z => z.id === gewaehltId) ?? null : null;
    const gewaehltEintrag = gewaehlt && suchpunkt ? eintraegeFuer([gewaehlt], suchpunkt)[0] : null;

    const setze = <K extends keyof TalentFilter>(schluessel: K, wert: TalentFilter[K]) =>
        setFilter(f => ({ ...f, [schluessel]: wert }));
    const aktiv = filterAktiv(filter);

    /** Aus der Liste gewählt: Karte schwenkt hin. */
    const waehleAusListe = useCallback((id: string) => {
        setMeldung(null);
        setGewaehltId(id);
        setFokus(f => ({ id, nr: (f?.nr ?? 0) + 1 }));
    }, []);
    /** Auf der Karte gewählt: die Karte bleibt, wo sie ist. */
    const waehleAufKarte = useCallback((id: string) => {
        setMeldung(null);
        setGewaehltId(id);
    }, []);

    const speichern = useCallback(
        (status: TalentStatus, notiz: string) => (gewaehltId ? speichereTalent(gewaehltId, status, notiz) : Promise.resolve()),
        [gewaehltId, speichereTalent],
    );
    const loeschen = useCallback(async () => {
        if (!gewaehltId) return;
        const ergebnis = await loescheTalent(gewaehltId);
        setGewaehltId(null);
        setMeldung(ergebnis.dateienFehler
            ? { ton: 'warnung', text: ergebnis.dateienFehler }
            : { ton: 'ok', text: 'Bewerbung gelöscht.' });
    }, [gewaehltId, loescheTalent]);

    return (
        <div data-glass-view="talentpool" className="p-4 sm:p-6 lg:h-full flex flex-col gap-4 animate-fade-in">
            {/* ── Kopf ── */}
            <div className="flex flex-wrap items-start justify-between gap-3 shrink-0">
                <div className="min-w-0">
                    <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                        <UserSearch className="w-6 h-6 text-indigo-600" /> Talentpool
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">Bewerbungen von gefas-mbh.de — mit Standort und Umkreis auf der Karte.</p>
                    {zeilen && zeilen.length > 0 && (
                        <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                            <span><strong className="text-slate-800 tabular-nums">{zahlen.gesamt}</strong> Bewerbungen</span>
                            <span><strong className="text-blue-700 tabular-nums">{zahlen.neu}</strong> neu</span>
                            {zahlen.ohneStandort > 0 && <span><strong className="text-amber-700 tabular-nums">{zahlen.ohneStandort}</strong> ohne Standort</span>}
                            {zahlen.loeschFaellig > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setFilter({ ...LEERER_FILTER, nurLoeschfaellig: true })}
                                    className="inline-flex items-center gap-1 font-semibold text-red-700 hover:underline"
                                >
                                    <AlertTriangle className="w-3.5 h-3.5" />
                                    <span className="tabular-nums">{zahlen.loeschFaellig}</span> mit erreichter Löschfrist
                                </button>
                            )}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    {(laedt || standMs > 0) && (
                        <span className="text-[11px] text-slate-400 tabular-nums" aria-live="polite">
                            {laedt ? 'Wird aktualisiert …' : `Stand ${uhrzeit(standMs)}`}
                        </span>
                    )}
                    <button
                        type="button"
                        onClick={neuLaden}
                        disabled={laedt}
                        aria-label="Aktualisieren"
                        title="Aktualisieren"
                        className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:text-indigo-600 disabled:hover:bg-transparent"
                    >
                        <RefreshCw className={`w-4 h-4 ${laedt ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* ── Hinweise ── */}
            {nichtEingerichtet && (
                <p className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3 shrink-0">
                    <Info className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" /> {nichtEingerichtet}
                </p>
            )}
            {ladeFehler && (
                <div className="flex flex-wrap items-center gap-3 bg-red-50 border border-red-200 rounded-xl p-3 shrink-0">
                    <p role="alert" className="text-xs text-red-700 flex-1 min-w-[12rem]">Nicht geladen: {ladeFehler}</p>
                    <Button variant="secondary" size="sm" onClick={neuLaden} disabled={laedt}>Erneut versuchen</Button>
                </div>
            )}
            {meldung && (
                <div
                    role={meldung.ton === 'warnung' ? 'alert' : 'status'}
                    className={`flex items-start gap-2 rounded-xl p-3 text-xs shrink-0 border ${
                        meldung.ton === 'warnung' ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50 border-emerald-100 text-emerald-800'
                    }`}
                >
                    {meldung.ton === 'warnung'
                        ? <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
                        : <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />}
                    <p className="flex-1">{meldung.text}</p>
                    <button type="button" onClick={() => setMeldung(null)} aria-label="Hinweis schließen" className="text-slate-400 hover:text-slate-600">
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}
            {gekappt && (
                <p className="text-[11px] text-slate-400 shrink-0">
                    Gezeigt werden die neuesten {TALENTPOOL_GRENZE.toLocaleString('de-DE')} Bewerbungen.
                </p>
            )}

            {!zeilen && !nichtEingerichtet && !ladeFehler && (
                <p className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Lädt …</p>
            )}

            {zeilen && (
                <>
                    {/* ── Filter ── */}
                    {zeilen.length > 0 && (
                        <div data-glass="filterleiste" className="bg-white p-3 rounded-2xl border border-slate-200 shadow-sm flex flex-wrap items-center gap-2 shrink-0">
                            <div className="relative group flex-1 min-w-[14rem]">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
                                <input
                                    type="text"
                                    value={filter.text}
                                    onChange={(e) => setze('text', e.target.value)}
                                    placeholder="Name, Ort oder E-Mail …"
                                    aria-label="Bewerbungen durchsuchen"
                                    className="w-full min-h-[40px] pl-10 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 outline-none transition-all"
                                />
                            </div>
                            <select value={filter.rolle} onChange={(e) => setze('rolle', e.target.value)} aria-label="Rolle" className={AUSWAHL}>
                                <option value="">Alle Rollen</option>
                                {rollen.map(r => <option key={r} value={r}>{rolleLabel(r)}</option>)}
                            </select>
                            <select value={filter.arbeitsform} onChange={(e) => setze('arbeitsform', e.target.value)} aria-label="Arbeitsform" className={AUSWAHL}>
                                <option value="">Alle Arbeitsformen</option>
                                {TALENT_ARBEITSFORMEN.map(a => <option key={a} value={a}>{ARBEITSFORM_LABEL[a]}</option>)}
                            </select>
                            <select value={filter.status} onChange={(e) => setze('status', e.target.value as StatusFilter)} aria-label="Status" className={AUSWAHL}>
                                <option value="">Alle Status</option>
                                <option value="offen">Offen (ohne Zusage/Absage)</option>
                                {TALENT_STATUS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                            </select>
                            {kampagnen.length > 0 && (
                                <select value={filter.kampagne} onChange={(e) => setze('kampagne', e.target.value)} aria-label="Kampagne" className={AUSWAHL}>
                                    <option value="">Alle Kampagnen</option>
                                    {kampagnen.map(k => <option key={k} value={k}>{k}</option>)}
                                </select>
                            )}
                            <label className="min-h-[40px] inline-flex items-center gap-2 px-3 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={filter.nurLoeschfaellig}
                                    onChange={(e) => setze('nurLoeschfaellig', e.target.checked)}
                                    className="accent-red-600"
                                />
                                Löschfrist erreicht
                            </label>
                            {aktiv && (
                                <Button variant="ghost" size="sm" onClick={() => setFilter(LEERER_FILTER)}>Zurücksetzen</Button>
                            )}
                            <span className="text-[11px] text-slate-400 tabular-nums ml-auto">
                                {gefiltert.length === alle.length ? `${alle.length} Bewerbungen` : `${gefiltert.length} von ${alle.length}`}
                            </span>
                        </div>
                    )}

                    {/* ── Liste/Detail links, Karte rechts; schmal: Karte oben ── */}
                    <div className="flex flex-col lg:flex-row gap-4 lg:flex-1 lg:min-h-0">
                        <div className="order-2 lg:order-1 lg:w-[400px] xl:w-[440px] lg:shrink-0 lg:overflow-y-auto lg:pr-1 pb-6 lg:pb-2">
                            {gewaehlt ? (
                                <TalentpoolDetail
                                    key={gewaehlt.id}
                                    zeile={gewaehlt}
                                    heute={heute}
                                    suchEintrag={gewaehltEintrag}
                                    onZurueck={() => setGewaehltId(null)}
                                    onSpeichern={speichern}
                                    onLoeschen={loeschen}
                                />
                            ) : (
                                <TalentpoolListe
                                    eintraege={eintraege}
                                    gesamt={alle.length}
                                    filterAktiv={aktiv}
                                    mitSuchpunkt={suchpunkt !== null}
                                    gewaehltId={gewaehltId}
                                    heute={heute}
                                    onWaehle={waehleAusListe}
                                    onFilterZuruecksetzen={() => setFilter(LEERER_FILTER)}
                                />
                            )}
                        </div>
                        <div className="order-1 lg:order-2 h-[420px] lg:h-auto lg:flex-1 lg:min-h-0">
                            <TalentpoolKarte
                                eintraege={eintraege}
                                gewaehltId={gewaehltId}
                                fokus={fokus}
                                suchpunkt={suchpunkt}
                                alleRinge={alleRinge}
                                anzahlErreicht={anzahlErreicht(eintraege)}
                                legende={legende}
                                onWaehle={waehleAufKarte}
                                onSuchpunkt={setSuchpunkt}
                                onAlleRinge={setAlleRinge}
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
